import { useMemo } from 'react';
import { Circle, CheckCircle2, Loader2, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { rangoDeHoy } from '@/lib/tareas';
import { useCompletarTarea, useTareasLista } from '@/hooks/useTareas';
import type { Tarea } from '@/types/tareas';
import { AvatarsAsignados, PrioridadBadge, RelacionChip, VencimientoTexto } from '@/components/tareas/TareaPiezas';

const MAX_MIAS = 200;

type GrupoClave = 'vencidas' | 'hoy' | 'semana' | 'despues' | 'sin_fecha';

const GRUPOS: { clave: GrupoClave; titulo: string; acento: string }[] = [
  { clave: 'vencidas', titulo: 'Vencidas', acento: 'text-red-600' },
  { clave: 'hoy', titulo: 'Hoy', acento: 'text-amber-600' },
  { clave: 'semana', titulo: 'Esta semana', acento: 'text-primary-600' },
  { clave: 'despues', titulo: 'Después', acento: 'text-text-primary' },
  { clave: 'sin_fecha', titulo: 'Sin fecha', acento: 'text-text-secondary' },
];

export function MisTareasView({ onAbrirTarea }: { onAbrirTarea: (id: string) => void }) {
  const { data, isLoading } = useTareasLista({ mias: true, completadas: 'no', limit: MAX_MIAS });
  const completar = useCompletarTarea();

  const grupos = useMemo(() => {
    const { hoyInicio, hoyFin, semanaFin } = rangoDeHoy();
    const resultado: Record<GrupoClave, Tarea[]> = { vencidas: [], hoy: [], semana: [], despues: [], sin_fecha: [] };
    (data?.items ?? []).forEach(t => {
      if (!t.fechaVencimiento) return resultado.sin_fecha.push(t);
      const f = new Date(t.fechaVencimiento);
      if (f < hoyInicio) resultado.vencidas.push(t);
      else if (f <= hoyFin) resultado.hoy.push(t);
      else if (f <= semanaFin) resultado.semana.push(t);
      else resultado.despues.push(t);
    });
    return resultado;
  }, [data]);

  if (isLoading) return <div className="py-16 flex justify-center text-gray-400"><Loader2 className="animate-spin" /></div>;

  if (!data?.items.length) {
    return (
      <div className="py-16 text-center">
        <Inbox className="mx-auto text-gray-300" size={40} />
        <p className="mt-3 text-sm font-medium text-text-primary">No tienes tareas pendientes</p>
        <p className="text-sm text-text-secondary">Las tareas que te asignen o crees sin asignar aparecerán aquí.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {data.total > data.items.length && (
        <p className="text-xs text-text-secondary">Mostrando {data.items.length} de {data.total} tareas. Usa la pestaña Lista para ver todas.</p>
      )}
      {GRUPOS.map(({ clave, titulo, acento }) => grupos[clave].length > 0 && (
        <section key={clave}>
          <h3 className={cn('text-sm font-semibold mb-2', acento)}>
            {titulo} <span className="text-text-secondary font-normal">({grupos[clave].length})</span>
          </h3>
          <ul className="space-y-1.5">
            {grupos[clave].map(t => (
              <li
                key={t.id}
                onClick={() => onAbrirTarea(t.id)}
                className="flex items-center gap-3 rounded-lg bg-white border border-border px-3 py-2.5 hover:border-primary-300 cursor-pointer"
              >
                <button
                  onClick={e => { e.stopPropagation(); completar.mutate({ id: t.id, completar: !t.completadaAt }); }}
                  className="text-gray-300 hover:text-emerald-500 flex-shrink-0"
                  aria-label="Marcar como completada"
                  title="Marcar como completada"
                >
                  {t.completadaAt ? <CheckCircle2 size={20} className="text-emerald-500" /> : <Circle size={20} />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{t.titulo}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                    <span className="text-[11px] text-text-secondary">{t.tableroNombre} · {t.columnaNombre}</span>
                    {t.relaciones.slice(0, 2).map(r => <RelacionChip key={r.id} relacion={r} compacta />)}
                  </div>
                </div>
                <PrioridadBadge prioridad={t.prioridad} className="hidden sm:inline-flex" />
                <VencimientoTexto tarea={t} className="hidden sm:inline-flex" />
                <AvatarsAsignados asignados={t.asignados} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
